/**
 * rankUtils.ts - Promoter Ranking and Reward Calculation Utilities
 *
 * This utility module handles complex promoter ranking logic including cascading
 * rank assignments, minimum donation requirements, and reward tier calculations.
 *
 * Key Functions:
 * - calculatePromoterRankLevel: Determines a promoter's reward tier
 * - assignPromotersToRankTiers: Assigns all promoters using cascading logic
 * - getPromotersForRankLevel: Gets promoters for a specific rank level
 *
 * Features:
 * - Cascading rank assignment (promoters who don't meet minimums bump others down)
 * - Minimum donation requirement enforcement
 * - Rank range support (e.g., "1", "2-3", "4-10")
 * - Effective rank calculation for display
 * - Fallback handling for unqualified promoters
 *
 * Dependencies: None (pure TypeScript interfaces and logic)
 */

import { PromoterWithRank } from "../models";

// Shared utility functions for rank-based reward calculations

export interface RankDefinition {
  id: string;
  rank: string;
  reward: string;
  minimum?: number;
  order: number;
}

export interface IPromoterWithRank {
  id: string;
  name: string;
  totalRaised: number;
  actualRank: number; // Their position in the leaderboard (1, 2, 3, etc.)
}

/**
 * Calculates which rank reward level a promoter qualifies for based on:
 * 1. Their actual leaderboard rank
 * 2. The minimum donation requirements for each rank level
 * 3. Available spots in each rank level
 * 4. Cascading effect where promoters who don't meet minimums bump others down
 *//**
 * Calculates the rank level and effective rank of a given promoter.
 */
export const calculatePromoterRankLevel = (
  promoter: PromoterWithRank,
  allPromoters: PromoterWithRank[],
  rankDefinitions: RankDefinition[]
): { rankLevel: RankDefinition | null; effectiveRank: number } => {
  const assignments = assignPromotersToRankTiers(allPromoters, rankDefinitions);
  const assignment = assignments.find((a) => a.promoter.id === promoter.id);

  if (assignment) {
    return {
      rankLevel: assignment.rankLevel,
      effectiveRank: assignment.effectiveRank,
    };
  }

  // Fallback
  return { rankLevel: null, effectiveRank: promoter.actualRank || 1 };
};

/**
 * Assigns all promoters to rank tiers using cascading logic.
 * Promoters who don't meet minimums for any tier remain unranked.
 */
export const assignPromotersToRankTiers = (
  allPromoters: PromoterWithRank[],
  rankDefinitions: RankDefinition[]
): Array<{
  promoter: PromoterWithRank;
  rankLevel: RankDefinition | null;
  effectiveRank: number;
}> => {
  // Sort promoters by total raised (descending)
  const sortedPromoters = allPromoters
    .slice()
    .sort((a, b) => b.totalRaised - a.totalRaised)
    .map((p, index) => new PromoterWithRank({ ...p, actualRank: index + 1 }));

  // Sort rank definitions by their start rank (ascending)
  const sortedRanks = rankDefinitions.slice().sort((a, b) => {
    const aStart =
      typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
    const bStart =
      typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
    return (aStart || 0) - (bStart || 0);
  });

  const assignments: Array<{
    promoter: PromoterWithRank;
    rankLevel: RankDefinition | null;
    effectiveRank: number;
  }> = [];

  for (const promoter of sortedPromoters) {
    let matchedRank: RankDefinition | null = null;

    // Assign to the highest qualifying rank
    for (const rank of sortedRanks) {
      const minimumRequired = rank.minimum || 0;

      // If promoter meets or exceeds the minimum for this rank, assign them
      if (promoter.totalRaised >= minimumRequired) {
        matchedRank = rank;
        break;
      }
    }

    assignments.push({
      promoter,
      rankLevel: matchedRank,
      effectiveRank: promoter.actualRank,
    });
  }

  return assignments;
};

/**
 * Returns all promoters (with assignment info) for a specific rank definition.
 */
export const getPromotersForRankLevel = (
  rankDefinition: RankDefinition,
  allPromoters: PromoterWithRank[],
  rankDefinitions: RankDefinition[]
): Array<{
  promoter: PromoterWithRank;
  rankLevel: RankDefinition | null;
  effectiveRank: number;
}> => {
  const assignments = assignPromotersToRankTiers(allPromoters, rankDefinitions);
  return assignments.filter((a) => a.rankLevel?.id === rankDefinition.id);
};
